"use client";

import { useEffect, useMemo, useState } from "react";

export type TeamHandoverPriority = "normal" | "important" | "urgent";
export type TeamHandoverCategory = "organization" | "patient" | "stock" | "safety" | "information";
export type TeamHandoverMessage = {
  id: number;
  title: string;
  body: string;
  category: TeamHandoverCategory;
  priority: TeamHandoverPriority;
  pharmacies: string[];
  audience: string;
  createdBy: string;
  publishDate: string;
  expiresDate: string;
  requiresAck: boolean;
  status: "active" | "archived";
  archivedAt: number | null;
  archivedBy: string;
  createdAt: number;
  updatedAt: number;
};
export type TeamHandoverRead = { id: number; handoverId: number; pharmacy: string; operator: string; readAt: number };
export type TeamHandoverData = { messages: TeamHandoverMessage[]; reads: TeamHandoverRead[]; syncedAt: number };
export type TeamOperator = { name: string; pharmacy: string; role?: string };

const parseList = (value: unknown) => {
  if (Array.isArray(value)) return value.map(String);
  try { return JSON.parse(String(value ?? "[]")) as string[]; } catch { return []; }
};

export const normalizeTeamHandoverData = (payload: { messages?: Record<string, unknown>[]; reads?: Record<string, unknown>[]; syncedAt?: number }): TeamHandoverData => ({
  messages: (payload.messages ?? []).map((row) => ({
    id: Number(row.id),
    title: String(row.title ?? ""),
    body: String(row.body ?? ""),
    category: String(row.category ?? "information") as TeamHandoverCategory,
    priority: String(row.priority ?? "normal") as TeamHandoverPriority,
    pharmacies: parseList(row.pharmacies),
    audience: String(row.audience ?? "Toute l’équipe"),
    createdBy: String(row.createdBy ?? row.created_by ?? ""),
    publishDate: String(row.publishDate ?? row.publish_date ?? ""),
    expiresDate: String(row.expiresDate ?? row.expires_date ?? ""),
    requiresAck: Boolean(row.requiresAck ?? row.requires_ack ?? 1),
    status: String(row.status ?? "active") as "active" | "archived",
    archivedAt: row.archivedAt || row.archived_at ? Number(row.archivedAt ?? row.archived_at) : null,
    archivedBy: String(row.archivedBy ?? row.archived_by ?? ""),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  })),
  reads: (payload.reads ?? []).map((row) => ({
    id: Number(row.id),
    handoverId: Number(row.handoverId ?? row.handover_id),
    pharmacy: String(row.pharmacy ?? ""),
    operator: String(row.operator ?? ""),
    readAt: Number(row.readAt ?? row.read_at ?? 0),
  })),
  syncedAt: Number(payload.syncedAt ?? Date.now()),
});

const todayIso = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};

export const isTeamHandoverVisible = (message: TeamHandoverMessage, date = todayIso()) =>
  message.status === "active" && message.publishDate <= date && (!message.expiresDate || message.expiresDate >= date);

const categoryMeta: Record<TeamHandoverCategory, { label: string; icon: string }> = {
  organization: { label: "Organisation", icon: "🧭" },
  patient: { label: "Patient / comptoir", icon: "🩺" },
  stock: { label: "Stock / commande", icon: "📦" },
  safety: { label: "Sécurité", icon: "🛡️" },
  information: { label: "Information", icon: "💬" },
};

type Draft = Omit<TeamHandoverMessage, "id" | "status" | "archivedAt" | "archivedBy" | "createdAt" | "updatedAt">;
const emptyDraft = (pharmacy: string, operator: string): Draft => ({
  title: "", body: "", category: "information", priority: "normal", pharmacies: [pharmacy],
  audience: "Toute l’équipe", createdBy: operator, publishDate: todayIso(), expiresDate: "", requiresAck: true,
});

export function TeamHandoverModule({ onBack, initialPharmacy, initialOperator, operators }: {
  onBack: () => void;
  initialPharmacy: string;
  initialOperator: string;
  operators: TeamOperator[];
}) {
  const [data, setData] = useState<TeamHandoverData>({ messages: [], reads: [], syncedAt: 0 });
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(initialPharmacy, initialOperator));
  const [editingId, setEditingId] = useState<number | null>(null);
  const [filter, setFilter] = useState<"active" | "unread" | "urgent" | "archived">("active");
  const [pharmacyFilter, setPharmacyFilter] = useState(initialPharmacy);
  const [operator, setOperator] = useState(initialOperator);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = async (quiet = false) => {
    try {
      const response = await fetch("/api/team-handovers", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setData(normalizeTeamHandoverData(payload));
      setError("");
    } catch (requestError) {
      if (!quiet) setError(requestError instanceof Error ? requestError.message : "Synchronisation impossible.");
    }
  };

  useEffect(() => {
    const initialLoad = window.setTimeout(() => void refresh(), 0);
    return () => window.clearTimeout(initialLoad);
  }, []);

  const activeMessages = data.messages.filter((message) => isTeamHandoverVisible(message) && message.pharmacies.includes(pharmacyFilter));
  const unread = activeMessages.filter((message) => !data.reads.some((read) => read.handoverId === message.id && read.pharmacy === pharmacyFilter && read.operator === operator));
  const visible = useMemo(() => data.messages.filter((message) => {
    if (!message.pharmacies.includes(pharmacyFilter)) return false;
    if (filter === "archived") return message.status === "archived";
    if (!isTeamHandoverVisible(message)) return false;
    if (filter === "urgent") return message.priority === "urgent";
    if (filter === "unread") return !data.reads.some((read) => read.handoverId === message.id && read.pharmacy === pharmacyFilter && read.operator === operator);
    return true;
  }), [data, filter, operator, pharmacyFilter]);

  const togglePharmacy = (name: string) => setDraft((current) => ({
    ...current,
    pharmacies: current.pharmacies.includes(name) ? current.pharmacies.filter((item) => item !== name) : [...current.pharmacies, name],
  }));

  const save = async () => {
    if (!draft.title.trim() || !draft.body.trim() || !draft.pharmacies.length) return;
    setBusy(true);
    try {
      const response = await fetch("/api/team-handovers", {
        method: editingId ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...draft, id: editingId }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setDraft(emptyDraft(pharmacyFilter, operator));
      setEditingId(null);
      await refresh(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Enregistrement impossible.");
    } finally { setBusy(false); }
  };

  const act = async (message: TeamHandoverMessage, action: "read" | "archive" | "reopen") => {
    setBusy(true);
    try {
      const response = await fetch("/api/team-handovers", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: message.id, action, operator, pharmacy: pharmacyFilter }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Enregistrement impossible.");
      await refresh(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Enregistrement impossible.");
    } finally { setBusy(false); }
  };

  const edit = (message: TeamHandoverMessage) => {
    setEditingId(message.id);
    setDraft({
      title: message.title, body: message.body, category: message.category, priority: message.priority,
      pharmacies: message.pharmacies, audience: message.audience, createdBy: message.createdBy,
      publishDate: message.publishDate, expiresDate: message.expiresDate, requiresAck: message.requiresAck,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (message: TeamHandoverMessage) => {
    if (!window.confirm(`Supprimer définitivement « ${message.title} » ?`)) return;
    setBusy(true);
    try {
      const response = await fetch(`/api/team-handovers?id=${message.id}`, { method: "DELETE" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Suppression impossible.");
      await refresh(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Suppression impossible.");
    } finally { setBusy(false); }
  };

  return <main className="handover-app">
    <header className="module-header">
      <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
      <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
      <div className="module-header-actions"><a className="module-tools-shortcut" href="/?tools=1"><span>🧰</span><b>Applications</b></a><span className="module-status"><i /> Temps réel</span></div>
    </header>
    <section className="handover-page">
      <section className="handover-hero">
        <div><span>🤝 RELAIS ENTRE ÉQUIPES</span><h1>Passation <em>équipe</em></h1><p>Une information claire, ciblée et tracée entre deux prises de poste.</p></div>
        <div className="handover-kpis"><article><strong>{activeMessages.length}</strong><span>actives</span></article><article><strong>{unread.length}</strong><span>à lire</span></article><article className="urgent"><strong>{activeMessages.filter((item) => item.priority === "urgent").length}</strong><span>urgentes</span></article></div>
      </section>

      <section className={`handover-composer ${draft.priority}`}>
        <header><div><span>{editingId ? "MODIFIER LA PASSATION" : "NOUVELLE PASSATION"}</span><h2>{editingId ? draft.title : "Que doit savoir l’équipe suivante ?"}</h2></div>{editingId && <button type="button" onClick={() => { setEditingId(null); setDraft(emptyDraft(pharmacyFilter, operator)); }}>Annuler la modification</button>}</header>
        <div className="handover-form">
          <label className="wide"><span>Titre court *</span><input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} placeholder="Ex. Commande Podowell attendue demain matin" /></label>
          <label className="wide"><span>Message utile *</span><textarea value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value.slice(0, 1800) })} placeholder="La situation, ce qui a déjà été fait et ce que l’équipe doit reprendre…" /></label>
          <label><span>Catégorie</span><select value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value as TeamHandoverCategory })}>{Object.entries(categoryMeta).map(([value, meta]) => <option key={value} value={value}>{meta.icon} {meta.label}</option>)}</select></label>
          <label><span>Destinataires</span><select value={draft.audience} onChange={(event) => setDraft({ ...draft, audience: event.target.value })}><option>Toute l’équipe</option><option>Titulaires</option><option>Pharmaciens</option><option>Préparateurs</option><option>Équipe du matin</option><option>Équipe de l’après-midi</option></select></label>
          <label><span>Publié par</span><select value={draft.createdBy} onChange={(event) => setDraft({ ...draft, createdBy: event.target.value })}>{operators.map((item) => <option key={item.name}>{item.name}</option>)}</select></label>
          <label><span>Afficher à partir du</span><input type="date" value={draft.publishDate} onChange={(event) => setDraft({ ...draft, publishDate: event.target.value })} /></label>
          <label><span>Masquer après le</span><input type="date" min={draft.publishDate} value={draft.expiresDate} onChange={(event) => setDraft({ ...draft, expiresDate: event.target.value })} /></label>
          <fieldset className="wide"><legend>Priorité</legend><div className="handover-priority">{(["normal", "important", "urgent"] as TeamHandoverPriority[]).map((value) => <button key={value} type="button" className={`${value} ${draft.priority === value ? "active" : ""}`} onClick={() => setDraft({ ...draft, priority: value })}><i />{value === "normal" ? "Information" : value === "important" ? "Importante" : "Urgente"}</button>)}</div></fieldset>
          <fieldset className="wide"><legend>Pharmacies concernées *</legend><div className="handover-pharmacies">{["Square", "Ragadan", "Laurentine"].map((name) => <button key={name} type="button" className={draft.pharmacies.includes(name) ? "active" : ""} onClick={() => togglePharmacy(name)}><i>{draft.pharmacies.includes(name) ? "✓" : ""}</i>{name}</button>)}</div></fieldset>
          <label className="handover-ack"><input type="checkbox" checked={draft.requiresAck} onChange={(event) => setDraft({ ...draft, requiresAck: event.target.checked })} /><span>Demander une confirmation de lecture</span></label>
        </div>
        <footer><p>📺 La passation sera relayée sur l’accueil et la TV des pharmacies sélectionnées.</p><button type="button" disabled={busy || !draft.title.trim() || !draft.body.trim() || !draft.pharmacies.length} onClick={() => void save()}>{busy ? "Publication…" : editingId ? "Enregistrer les modifications" : "Publier la passation"}</button></footer>
      </section>

      <section className="handover-board">
        <header>
          <div><span>PASSATIONS</span><h2>À reprendre entre équipes</h2></div>
          <div className="handover-controls"><select value={pharmacyFilter} onChange={(event) => setPharmacyFilter(event.target.value)}><option>Square</option><option>Ragadan</option><option>Laurentine</option></select><select value={operator} onChange={(event) => setOperator(event.target.value)}>{operators.map((item) => <option key={item.name}>{item.name}</option>)}</select></div>
        </header>
        <nav>{([["active", "Actives"], ["unread", `À lire (${unread.length})`], ["urgent", "Urgentes"], ["archived", "Archives"]] as const).map(([value, label]) => <button type="button" key={value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>{label}</button>)}</nav>
        {error && <div className="handover-error">{error}<button type="button" onClick={() => void refresh()}>Réessayer</button></div>}
        <div className="handover-list">{visible.length ? visible.map((message) => {
          const meta = categoryMeta[message.category];
          const read = data.reads.some((item) => item.handoverId === message.id && item.pharmacy === pharmacyFilter && item.operator === operator);
          const readCount = data.reads.filter((item) => item.handoverId === message.id && item.pharmacy === pharmacyFilter).length;
          return <article className={`${message.priority} ${read ? "read" : "unread"}`} key={message.id}>
            <div className="handover-card-icon">{meta.icon}</div>
            <div className="handover-card-main">
              <header><div><span>{meta.label} · {message.pharmacies.join(" · ")}</span><h3>{message.title}</h3></div><b>{message.priority === "urgent" ? "Urgent" : message.priority === "important" ? "Important" : read ? "Lu" : "À lire"}</b></header>
              <p>{message.body}</p>
              <div className="handover-meta"><span>Par <strong>{message.createdBy}</strong></span><span>Pour <strong>{message.audience}</strong></span><span>{message.publishDate.split("-").reverse().join("/")}</span>{message.requiresAck && <span>👁 {readCount} lecture{readCount > 1 ? "s" : ""}</span>}</div>
              <footer>{message.status === "active" && !read && <button className="ack" type="button" disabled={busy} onClick={() => void act(message, "read")}>{busy ? "Enregistrement…" : "✓ Vu et pris en compte"}</button>}{message.status === "active" ? <><button type="button" disabled={busy} onClick={() => edit(message)}>Modifier</button><button type="button" disabled={busy} onClick={() => void act(message, "archive")}>Archiver</button></> : <button type="button" disabled={busy} onClick={() => void act(message, "reopen")}>Réactiver</button>}<button className="delete" type="button" disabled={busy} onClick={() => void remove(message)}>Supprimer</button></footer>
            </div>
          </article>;
        }) : <div className="handover-empty"><span>✓</span><h3>Aucune passation dans cette vue</h3><p>L’équipe est à jour pour {pharmacyFilter}.</p></div>}</div>
      </section>
    </section>
  </main>;
}
